/**
 * Lazy RevenueCat Web SDK initializer.
 *
 * The SDK is configured at most once per app-user-id. Subsequent calls
 * return the cached singleton. We do NOT initialise on app boot — only
 * when the user actually clicks "Subscribe" so the bundle stays light
 * for users who never visit /subscriptions.
 */
import { Purchases } from "@revenuecat/purchases-js";

let _instance = null;
let _initKey = ""; // `${apiKey}:${appUserId}` — busts cache on user switch

export async function getPurchasesInstance({ apiKey, appUserId }) {
    if (!apiKey || !appUserId) {
        throw new Error("RevenueCat: apiKey and appUserId are required");
    }
    const cacheKey = `${apiKey}:${appUserId}`;
    if (_instance && _initKey === cacheKey) return _instance;

    if (Purchases.isConfigured && Purchases.isConfigured() && _initKey === cacheKey) {
        _instance = Purchases.getSharedInstance();
        return _instance;
    }

    // If a different user was previously configured, swap via changeUser
    if (_instance && _initKey && _initKey !== cacheKey) {
        try {
            await _instance.changeUser(appUserId);
            _initKey = cacheKey;
            return _instance;
        } catch {
            // Fall through to fresh configure (e.g. apiKey changed too).
        }
    }

    _instance = Purchases.configure({ apiKey, appUserId });
    _initKey = cacheKey;
    return _instance;
}

/**
 * Fetch offerings and locate the package whose product matches the
 * given RevenueCat product id. Returns ``null`` when no match exists
 * so the caller can fall back to the hosted purchase link.
 */
export async function findPackageForProduct(purchases, productId) {
    if (!purchases || !productId) return null;
    let offerings;
    try {
        offerings = await purchases.getOfferings();
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("RevenueCat: getOfferings failed", err);
        return null;
    }
    const buckets = [];
    if (offerings?.current) buckets.push(offerings.current);
    if (offerings?.all) buckets.push(...Object.values(offerings.all));
    for (const off of buckets) {
        for (const pkg of off?.availablePackages || []) {
            const ident =
                pkg?.webBillingProduct?.identifier ||
                pkg?.rcBillingProduct?.identifier ||
                pkg?.product?.identifier ||
                pkg?.identifier ||
                "";
            if (ident === productId) return pkg;
        }
    }
    return null;
}
